import React, { useState } from 'react';
import { User, Role } from '../types';
import { api } from '../services/api';
import { X, ShieldAlert } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [role, setRole] = useState<Role>('applicant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [discordTag, setDiscordTag] = useState('');
  const [minecraftIgn, setMinecraftIgn] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const res = await api.login({ email, password });
        onSuccess(res.user);
        onClose();
      } else {
        const res = await api.register({
          email,
          password,
          name,
          role,
          discord_tag: discordTag,
          minecraft_ign: minecraftIgn,
        });
        onSuccess(res.user);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800 }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            {mode === 'login'
              ? 'Sign in to track applications & opportunities'
              : 'Register to apply for open positions at SansCraft'}
          </p>
        </div>

        {error && (
          <div
            style={{
              background: 'rgba(244, 63, 94, 0.1)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--accent-rose)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.85rem',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ShieldAlert size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <div className="form-group">
                <label className="form-label">Account Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <button
                    type="button"
                    className={`btn-secondary ${role === 'applicant' ? 'active' : ''}`}
                    style={{
                      justifyContent: 'center',
                      borderColor: role === 'applicant' ? 'var(--color-aqua)' : 'var(--color-beige-border)',
                      color: role === 'applicant' ? 'var(--color-aqua)' : 'var(--text-muted)',
                    }}
                    onClick={() => setRole('applicant')}
                  >
                    Applicant
                  </button>
                  <button
                    type="button"
                    className={`btn-secondary ${role === 'hiring_team' ? 'active' : ''}`}
                    style={{
                      justifyContent: 'center',
                      borderColor: role === 'hiring_team' ? 'var(--color-beige-warm)' : 'var(--color-beige-border)',
                      color: role === 'hiring_team' ? 'var(--color-beige-warm)' : 'var(--text-muted)',
                    }}
                    onClick={() => setRole('hiring_team')}
                  >
                    HR / Hiring Team
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Alex Vance"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-input"
              placeholder="you@sanscraft.top"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {mode === 'register' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Discord Handle</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Alex#1337"
                  value={discordTag}
                  onChange={(e) => setDiscordTag(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Minecraft IGN</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. VanceDev"
                  value={minecraftIgn}
                  onChange={(e) => setMinecraftIgn(e.target.value)}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: '10px' }}
            disabled={loading}
          >
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center', borderTop: '1px solid var(--color-beige-border)', paddingTop: '16px' }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {mode === 'login' ? "Don't have an account?" : 'Already registered?'}
            <button
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-aqua)',
                fontWeight: 700,
                cursor: 'pointer',
                marginLeft: '6px',
              }}
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
              }}
            >
              {mode === 'login' ? 'Register Now' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
